import { useState, useEffect } from 'react';
import { usersService, rolePermissionsService } from '../services/firestoreService';
import { getDefaultPermissions, mergePermissions } from '../utils/rolePermissions';
import { configHelpers } from '../config/app';

function readSessionRole() {
  try {
    return typeof window !== 'undefined' ? sessionStorage.getItem('userRole') : null;
  } catch {
    return null;
  }
}

/**
 * Hook to get current user's role and permissions
 * 
 * @returns {Object} { role, permissions, loading, error }
 */
export const useUserRole = () => {
  // Use session role immediately so sidebar RBAC matches the logged-in user before Firestore loads
  const [role, setRole] = useState(() => readSessionRole() || 'sales');
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUserRole = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load custom role defaults from Firestore first
        let customDefaults = null;
        try {
          customDefaults = await rolePermissionsService.getDefaultRolePermissions();
        } catch (error) {
          console.error('Error loading custom role defaults:', error);
          // Continue with hardcoded defaults
        }

        // Get current user info from sessionStorage
        const username = sessionStorage.getItem('username') || sessionStorage.getItem('userEmail');
        const storedRole = sessionStorage.getItem('userRole');
        const authType = sessionStorage.getItem('authType');
        
        // If no username, user is not logged in
        if (!username) {
          setRole('sales'); // Default
          setPermissions(getDefaultPermissions('sales', customDefaults));
          setLoading(false);
          return;
        }

        // Handle hardcoded owner/admin user
        if (username === 'owner' && authType === 'admin') {
          setRole('owner');
          setPermissions(getDefaultPermissions('owner', customDefaults));
          setLoading(false);
          return;
        }

        // If role is already in sessionStorage, use it temporarily
        if (storedRole) {
          setRole(storedRole);
          setPermissions(getDefaultPermissions(storedRole, customDefaults));
        }

        // Try to get user from Firestore to get full user data including custom permissions
        try {
          const user = await usersService.getByUsername(username);
          
          if (user) {
            let userRole = user.role || storedRole || 'sales';
            
            // Auto-set admin role for admin email domain (god mode)
            if (configHelpers.isAdminEmail(user.email)) {
              userRole = 'admin';
              console.log('Auto-setting admin role for admin email domain:', user.email);
              // Update role in Firestore if it's not already admin
              if (user.role !== 'admin') {
                try {
                  await usersService.update(user.id, { role: 'admin' });
                  console.log('Updated user role to admin in Firestore');
                } catch (err) {
                  console.error('Error updating role to admin:', err);
                }
              }
            }
            
            setRole(userRole);
            
            // Merge custom permissions with default permissions
            // Use custom role defaults if available, otherwise use hardcoded defaults
            const userPermissions = user.permissions || {};
            console.log('Loading user permissions - custom:', userPermissions);
            const mergedPermissions = mergePermissions(userRole, userPermissions, customDefaults);
            console.log('Loading user permissions - merged:', mergedPermissions);
            setPermissions(mergedPermissions);
            
            // Update sessionStorage with role
            if (userRole) {
              sessionStorage.setItem('userRole', userRole);
            }
          } else {
            // User not found in Firestore, use stored role or default
            const fallbackRole = storedRole || 'sales';
            setRole(fallbackRole);
            setPermissions(getDefaultPermissions(fallbackRole, customDefaults));
          }
        } catch (firestoreError) {
          console.error('Error loading user from Firestore:', firestoreError);
          // Use stored role or default
          const fallbackRole = storedRole || 'sales';
          setRole(fallbackRole);
          setPermissions(getDefaultPermissions(fallbackRole, customDefaults));
        }
      } catch (err) {
        console.error('Error in useUserRole:', err);
        setError(err.message);
        // Set defaults on error
        setRole('sales');
        // Try to get custom defaults even on error
        let errorCustomDefaults = null;
        try {
          errorCustomDefaults = await rolePermissionsService.getDefaultRolePermissions();
        } catch (e) {
          // Ignore error, use hardcoded defaults
        }
        setPermissions(getDefaultPermissions('sales', errorCustomDefaults));
      } finally {
        setLoading(false);
      }
    };

    loadUserRole();

    // Listen for role changes (e.g., when user is updated in settings)
    const handleStorageChange = (e) => {
      if (e.key === 'userRole' || e.key === 'username') {
        loadUserRole();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (for same-tab updates)
    const handleRoleUpdate = () => {
      loadUserRole();
    };
    window.addEventListener('userRoleUpdated', handleRoleUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userRoleUpdated', handleRoleUpdate);
    };
  }, []);

  return { role, permissions, loading, error };
};

